/**
 * Warstwa API modułu: K — dokumenty działu (wzory + wydania scope dept).
 * Właściciel: FALA 1, agent K-1/K-2 (K-3 dopisuje rejestr HR).
 * Trasy backendu: `api/document-templates`, `api/document-issues`.
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod doczepianych do wspólnego `apiService`. Nazwy muszą być unikalne
 * w całej aplikacji — kolizja zatrzymuje start (patrz attachApiModules).
 */
export function attach(api) {
    return {
        // ── WZORY (/admin/hr-config?tab=documents → „Wzory dokumentów"; scope hr dokłada I-a) ───────
        getDocumentTemplates: (scope = "dept", q, includeArchived = false) =>
            api.get("/document-templates", { params: { scope, q: q || undefined, includeArchived } }),
        getDocumentTemplate: (id) => api.get(`/document-templates/${id}`),
        createDocumentTemplate: (dto) => api.post("/document-templates", dto),
        updateDocumentTemplate: (id, dto) => api.put(`/document-templates/${id}`, dto),
        setDocumentTemplateActive: (id, isActive) => api.patch(`/document-templates/${id}/active`, { isActive }),
        deleteDocumentTemplate: (id) => api.delete(`/document-templates/${id}`),

        getDocumentTemplateVersions: (id) => api.get(`/document-templates/${id}/versions`),
        restoreDocumentTemplateVersion: (id, version) =>
            api.post(`/document-templates/${id}/versions/${version}/restore`),

        // Przypisania przychodzą razem ze szczegółem wzoru (GET {id}) — osobne pobranie byłoby
        // drugim źródłem tej samej prawdy i drugim miejscem do zapomnienia o odświeżeniu.
        setDocumentTemplateAssignments: (id, assignments) =>
            api.put(`/document-templates/${id}/assignments`, assignments),

        // [2026-09-23] Zakładka „Protokoły sprzętu” w /admin/hr-config: czy regulamin jest domyślnie dołączany do
        // protokołu przekazania. Ta sama bramka, co wzory zatrudnienia (HrAccess). Tu, a nie w apiService.js, bo warstwą
        // API modułu wzorów jest TEN plik (zapadka DocumentIssueWarstwaBezWpieciaTests: każda trasa kontrolera wzorów
        // ma tu swoje wpięcie).
        getUstawieniaProtokolowSprzetu: () => api.get("/document-templates/protokoly-sprzetu/ustawienia"),
        saveUstawieniaProtokolowSprzetu: (data) => api.put("/document-templates/protokoly-sprzetu/ustawienia", data),
        getDocumentTemplateCategories: (scope = "dept", onlyActive = false) =>
            api.get("/document-templates/categories", { params: { scope, onlyActive } }),
        createDocumentTemplateCategory: (dto) => api.post("/document-templates/categories", dto),
        updateDocumentTemplateCategory: (id, dto) => api.put(`/document-templates/categories/${id}`, dto),
        deleteDocumentTemplateCategory: (id) => api.delete(`/document-templates/categories/${id}`),

        /** Wydruk próbny w kreatorze — HTML tekstem, front wkłada go do ramki z sandboxem. */
        renderDocumentTemplatePreview: (payload) =>
            api.post("/document-templates/render-preview", payload, { responseType: "text" }),

        /** Wzory, z których TEN użytkownik może wydać dokument (aktywne, z treścią, przypisane). */
    /* [polecenie właściciela 2026-09-06, pkt 6] Decyzja akceptanta w sprawie wydanego dokumentu.
       „Dokument powinien trafić do działu HR, do podpisu lub do innej osoby wskazanej, żeby to
        zaakceptował." Bez wpięcia w tę warstwę endpoint byłby martwy — pilnuje tego zapadka
       KazdaTrasaKontrolerow_MaOdpowiednikWWarstwieApi. */
    rozstrzygnijAkceptacjeDokumentu: (issueId, zgoda, powod) =>
        api.post(`/document-issues/${issueId}/approval`, { zgoda, powod: powod || null }),

        getIssuableDocumentTemplates: () => api.get("/document-templates/issuable"),

        // ── WYDANIA (/moj-dzial/dokumenty) ─────────────────────────────────────────────────────
        getDeptDocumentIssues: (params) => api.get("/document-issues/dept", { params }),
        getDocumentIssue: (id) => api.get(`/document-issues/${id}`),
        previewDocumentIssue: (payload) =>
            api.post("/document-issues/preview", payload, { responseType: "text" }),
        createDocumentIssue: (payload) => api.post("/document-issues", payload),
        cancelDocumentIssue: (id, reason) => api.post(`/document-issues/${id}/cancel`, { reason }),

        /**
         * ZAPISANY plik wydania. `responseType: "blob"` jest tu obowiązkowe: bez niego axios
         * potraktowałby PDF jako tekst i plik doszedłby uszkodzony — a dokument o mocy prawnej
         * ma dojść bajt w bajt taki, jaki został wydany (reguła właściciela #2).
         */
        getDocumentIssuePdf: (id) => api.get(`/document-issues/${id}/pdf`, { responseType: "blob" }),
        getDocumentIssueAccessLog: (id) => api.get(`/document-issues/${id}/access-log`),

        // ── [P-1 / P-2, polecenie właściciela 2026-09-09] UMOWY: ścieżka akceptacji, e-mail z hasłem, skan ──
        /** Wydania czekające na krok, który rozstrzyga zalogowany (zakładka „Do mojej akceptacji"). */
        getDocumentIssuesToApprove: () => api.get("/document-issues/to-approve"),
        /** Hasło do PDF zapisane przy umowie (wydający i kadry) — puste, gdy jeszcze nie nadane. */
        getDocumentIssuePassword: (id) => api.get(`/document-issues/${id}/password`),
        /** Nadaje 4-cyfrowe hasło (raz, na stałe przy umowie) i oddaje je do pokazania. */
        ensureDocumentIssuePassword: (id) => api.post(`/document-issues/${id}/password`),
        /**
         * Dwie wiadomości: PDF zaszyfrowany hasłem (+ ewentualnie skan) i OSOBNO hasło.
         * [PII-10] `opcje.passwordChannel`: "email" (domyślnie, P-1) albo "none" (hasło NIE idzie mailem);
         * `opcje.confirmForeignDomain`: wydający potwierdził adres w innej domenie niż e-mail kontrahenta
         * (bez tego serwer odpowiada 400 z kodem INNA_DOMENA_ODBIORCY i nic nie wysyła).
         */
        emailDocumentIssue: (id, recipient, includeSignedScan = false, opcje = {}) =>
            api.post(`/document-issues/${id}/email`, {
                recipient,
                includeSignedScan: !!includeSignedScan,
                passwordChannel: opcje.passwordChannel || "email",
                confirmForeignDomain: !!opcje.confirmForeignDomain,
            }),
        /**
         * Podpisane egzemplarze do TEGO SAMEGO wydania (PDF/PNG/JPG) — jedna umowa, dwa podpisy.
         *
         * [OBSZAR 7 2026-09-17] Przyjmuje JEDEN plik albo tablicę (max 3 — limit egzekwuje serwer).
         * Klucz `files` i tak trafia do `Request.Form.Files`, więc stary klucz `file` też by zadziałał;
         * zostaje `files`, bo mówi prawdę o tym, co wysyłamy.
         */
        uploadDocumentIssueSignedScan: (id, files) => {
            const dane = new FormData();
            (Array.isArray(files) ? files : [files]).filter(Boolean).forEach((f) => dane.append("files", f));
            return api.post(`/document-issues/${id}/signed-scan`, dane);
        },
        /** Zapisany skan — `blob`, bo to plik (PDF albo zdjęcie), nie tekst. Bez numeru = pierwszy egzemplarz. */
        getDocumentIssueSignedScan: (id, ordinal) => api.get(
            ordinal && ordinal > 1 ? `/document-issues/${id}/signed-scan/${ordinal}` : `/document-issues/${id}/signed-scan`,
            { responseType: "blob" },
        ),

        // ── [polecenie właściciela 2026-09-14] PODPIS ONLINE: link e-mail + kod SMS, ohasłowany PDF z kartą podpisu ──
        /** Stan podpisów online przy wydaniu (najnowszy pierwszy) z osią zdarzeń: co, gdzie (IP), jak (urządzenie, kanał). */
        getDocumentIssuePodpisOnline: (id) => api.get(`/document-issues/${id}/podpis-online`),
        /** Wysyła e-mail z linkiem do strony podpisującej: { imieNazwisko, email, telefon, waznoscDni }. */
        sendDocumentIssuePodpisOnline: (id, dane) => api.post(`/document-issues/${id}/podpis-online`, dane),
        /** Anuluje aktywny link — strona podpisująca zobaczy „anulowano”. */
        cancelDocumentIssuePodpisOnline: (id) => api.post(`/document-issues/${id}/podpis-online/anuluj`),
        /** Nowy token i nowa ważność; e-mail idzie ponownie (także dla wygasłego linku). */
        resendDocumentIssuePodpisOnline: (id) => api.post(`/document-issues/${id}/podpis-online/wyslij-ponownie`),
        /** Egzemplarz z kartą podpisu (po podpisie) — `blob`, bo to PDF wydania. */
        getDocumentIssuePodpisOnlinePdf: (id) => api.get(`/document-issues/${id}/podpis-online/pdf`, { responseType: "blob" }),

        /**
         * Wydania, ktorych podmiotem jest zalogowany — zrodlo galezi „issue" w „Moich dokumentach HR".
         * Metoda mieszka TUTAJ, bo tak wskazuje komentarz w pages/hr/HrMyDocumentsPage.js: strona
         * osloniła wywolanie `typeof ... === "function"` i az do dopisania tej linii pokazywala PUSTA
         * sekcje wydan — czyli myliła „nie mam dokumentow" z „nikt nie wpiął endpointu".
         */
        getMyDocumentIssues: () => api.get("/document-issues/my"),

        /** Sonda silnika PDF — „czy da się w ogóle wydać dokument na tym serwerze". */
        getDocumentPdfHealth: () => api.get("/document-issues/health-pdf"),

        // ── REJESTR KADR (/admin/issued-documents → „Dokumenty inne" i „Struktura") ─────────────
        /** Rejestr wydań z filtrami; stronicowanie liczy serwer (zwraca items/total/page/pageSize). */
        getDocumentIssuesRegistry: (params) => api.get("/document-issues/registry", { params }),

        /** Słowniki do szuflady filtrów — tylko wartości występujące w rejestrze, z licznikami. */
        getDocumentIssuesRegistryFilters: (sourceKind = "dept") =>
            api.get("/document-issues/registry/filters", { params: { sourceKind } }),

        /**
         * Eksport XLSX rejestru z AKTUALNYMI filtrami. `responseType: "blob"` obowiązkowo — bez niego
         * axios potraktowałby arkusz jako tekst i pobrany plik nie otworzyłby się w Excelu.
         */
        exportDocumentIssuesRegistry: (params) =>
            api.get("/document-issues/registry/export", { params, responseType: "blob" }),

        /** Struktura: kategoria → wzory → liczby wydań (zakładka „Struktura"). */
        getDocumentTemplateStructure: (scope = "dept") =>
            api.get("/document-templates/structure", { params: { scope } }),
    };
}
